import {CSSProperties} from 'aphrodite';

import {RetypeColumnType} from 'data-frames/header-menu/DataFrameHeaderMenuOptions';
import {LeafType, LeafTypeToFriendlyName} from 'data-types/benchling-type-def/BenchlingTypeDef';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {GRAY_LIGHTER} from 'ui-library/Colors';

// Keep in sync with benchling/data_frames/constants.py
export const DATA_FRAME_DISPLAY_ROW_LIMIT = 50000;

export const AnalysisToolPreferences = {
  PROJECT_ID: 'analysisTool.projectId',
  FOLDER_ID: 'analysisTool.folderId',
};

export const ANALYSIS_TOOL_SPACING = 12;

/** Shared styles for a main view component that has:
 * - A box shadow
 * - ANALYSIS_TOOL_SPACING margin
 * - Rounded borders
 * - Grows up to the height of the parent
 */
export const SHARED_MAIN_VIEW_COMPONENT_WITH_MARGIN_STYLES = {
  borderRadius: 4,
  boxShadow: '0px 0px 4px rgb(92, 124, 131, 20%)',
  display: 'flex',
  flexDirection: 'column',
  margin: ANALYSIS_TOOL_SPACING,
  // Use maxHeight instead of height so that elements that are shorter than the height of the main view do not
  // grow taller than necessary.
  // Subtract out the vertical padding because a full 100% of the parent container would let the child grow
  // 2 * ANALYSIS_TOOL_SPACING too tall.
  maxHeight: `calc(100% - ${2 * ANALYSIS_TOOL_SPACING}px)`,
} satisfies CSSProperties;

export const ADD_ICON_WITH_HOVER_STYLES: CSSProperties = {
  marginLeft: 8,
  padding: 2,
  ':hover': {
    borderRadius: 4,
    background: GRAY_LIGHTER,
  },
};

export const ARCHIVED_ANALYSIS_TOOLTIP_TEXT = 'Editing is disabled on archived analyses';
export const ARCHIVED_ANALYSIS_TEMPLATE_TOOLTIP_TEXT = 'Editing is disabled on archived analyses template';
export const ARCHIVED_ANALYSIS_BANNER_TEXT = `${ARCHIVED_ANALYSIS_TOOLTIP_TEXT}.`;
export const PUBLISHED_ANALYSIS_TEMPLATE_TOOLTIP_TEXT = 'Editing is disabled on published analysis templates';

export const DISABLED_MESSAGE_STILL_RUNNING = 'The analysis is still running';

export const PILL_DISABLED_DUE_TO_DOWNSTREAM_FLAG_ROWS = 'Cannot edit due to downstream flag rows step';

// Keep in sync with benchling/pipelines/steps/models/pipeline_step.py's update status
export const ANCESTOR_STEP_FAILED_ERROR_MESSAGE = 'Ancestor step is FAILED.';

/** List of step types that create Benchling data outside of the pipeline.
 *
 * Keep in sync with list of step types in `benchling/pipelines/step_type_library/step_types.py`.
 */
export const ANALYSIS_OUTPUT_STEP_TYPES = [
  PipelineStepType.BULK_IMPORT,
  PipelineStepType.EXPORT_DATA_CATALOG_DATASET,
  PipelineStepType.EXPORT_DATA_CATALOG_FILE,
  PipelineStepType.LEGACY_DATA_IMPORT_ENTRY_CREATION,
  PipelineStepType.SAVE_TO_RESULTS,
  PipelineStepType.SEND_TO_DOCUMENT,
] as const;

/** List of step types that cannot be executed generically
 *
 * Keep in sync with the list of step types in `benchling/pipelines/step_execution_library/step_execution_constants.py`
 */
export const NON_EXECUTABLE_STEP_TYPES = [
  PipelineStepType.NOTEBOOK_TABLE_DATASET,
  PipelineStepType.PARAMETER_OPTIMIZATION_PREDICTION,
  PipelineStepType.TEMPLATE_INPUT,
  PipelineStepType.THIRD_PARTY,
] as const;

/**
 * Step types that cannot be refreshed by users.
 *
 * This includes step types that aren't supported by the step execution library (NON_EXECUTABLE_STEP_TYPES)
 * and step types that – while technically rerunnable – don't make sense to refresh, since their output data
 * wouldn't change.
 */
export const NON_REFRESHABLE_STEP_TYPES = [
  ...NON_EXECUTABLE_STEP_TYPES,
  PipelineStepType.DATA_CATALOG_DATASET,
  PipelineStepType.DATA_CATALOG_FILE,
  PipelineStepType.DATA_CATALOG_FILES,
  PipelineStepType.UPLOAD_CSV_DATASET,
] as const;

// Keep this list in sync with `benchling/pipelines/step_type_library/step_types.py`
export const TRANSFORMATION_STEP_TYPES = [
  PipelineStepType.AGGREGATE_DATASET,
  PipelineStepType.BIN_DATA,
  PipelineStepType.COMPUTED_COLUMN,
  PipelineStepType.FILTER_DATASET,
  PipelineStepType.FIND_AND_REPLACE,
  PipelineStepType.FLAG_ROWS,
  PipelineStepType.HIDE_COLUMNS,
  PipelineStepType.MAP_COLUMNS,
  PipelineStepType.PIVOT_DATASET,
  PipelineStepType.REGRESSION_INTERPOLATION,
  PipelineStepType.RENAME_COLUMNS,
  PipelineStepType.REORDER_COLUMNS,
  PipelineStepType.RETYPE_COLUMNS,
  PipelineStepType.SORT_COLUMNS,
  PipelineStepType.UNPIVOT_DATASET,
  PipelineStepType.WINDOW_FUNCTIONS,
];

export const TYPES_TO_DISPLAY_NAMES: {[k in RetypeColumnType]: string} = {
  INT: LeafTypeToFriendlyName[LeafType.INTEGER],
  DOUBLE: LeafTypeToFriendlyName[LeafType.DECIMAL],
  STRING: LeafTypeToFriendlyName[LeafType.TEXT],
  BOOLEAN: LeafTypeToFriendlyName[LeafType.BOOLEAN],
};
